import { LabClassifier } from './classifiers/LabClassifier';
import { LabTextGenerator } from './generators/LabTextGenerator';
import { LabConclusionGenerator } from './generators/LabConclusionGenerator';
import { TONE_RULES, TEMPERATURE_RULES } from '../constants/rules';

export class LabDetailedAnalyzer {
    constructor(language = 'en') {
        this.language = language;
        
        this.textGenerator = new LabTextGenerator(language);
        this.conclusionGenerator = new LabConclusionGenerator(language);
        this.analysisResults = {
            global: null,
            partitions: {},
            conclusions: {
                global: null,
                partitions: {},
                overall: null,
            },
            structuredConclusions: {
                global: null,
                partitions: {},
            },
        };
    }

    setLanguage(language) {
        if (language !== 'zh' && language !== 'en') {
            console.warn('不支持的语言类型，将使用默认中文');
            language = 'zh';
        }
        
        this.language = language;
        this.textGenerator.setLanguage(language);
        this.conclusionGenerator.setLanguage(language);
    }

    // Analyze individual channel data
    analyzeChannel(channelData, channelName) {
        if (!channelData) {
            return {
                statistics: {
                    mean: null,
                    median: null,
                    mode: null,
                    q1: null,
                    q3: null,
                    min: null,
                    max: null,
                    skewness: null,
                    kurtosis: null,
                    variance: null,
                    range: null,
                    stdDev: null,
                    iqr: null,
                },
                classification: {
                    brightnessLevel: null,
                    dynamicRange: null,
                    skewnessClass: null,
                    kurtosisClass: null,
                    mainTone: null,
                    colorTemperatureTone: null,
                    colorTemperatureIntensity: null,
                    colorTemperatureContrast: null,
                    localColorHighlights: null,
                    localColorLowlights: null,
                    localColorBalance: null,
                },
            };
        }

        const {
            mean,
            median,
            mode,
            q1,
            q3,
            min,
            max,
            skewness,
            kurtosis,
            variance,
        } = channelData;

        const range = max - min;
        const stdDev = Math.sqrt(variance);
        const iqr = q3 - q1;
        
        let classification = {};

        if (channelName === "L") {
            classification.brightnessLevel = LabClassifier.classifyBrightness(mean);
            classification.dynamicRange = LabClassifier.classifyRange(range);
            classification.skewnessClass = LabClassifier.classifySkewness(skewness);
            classification.kurtosisClass = LabClassifier.classifyKurtosis(kurtosis);
        } else {
            if (channelName === "a") {
                classification.mainTone = LabClassifier.classifyATone(mean);
            } else if (channelName === "b") {
                classification.colorTemperatureTone = LabClassifier.classifyBTone(mean);
            }
        }

        return {
            statistics: {
                mean,
                median,
                mode,
                q1,
                q3,
                min,
                max,
                skewness,
                kurtosis,
                variance,
                range,
                stdDev,
                iqr,
            },
            classification,
        };
    }

    generateAnalysisConclusion(analysis, partitionName) {
        const prefix = this.language === 'zh' 
            ? (partitionName === "global" ? "整体" : `${partitionName} 区域`)
            : (partitionName === "global" ? "Overall" : `${partitionName} area`);

        // 检查是否所有通道都为空
        if (!analysis.L && !analysis.a && !analysis.b) {
            // const noData = this.language === 'zh' ? "无数据" : "no data";
            const skipAnalysis = this.language === 'zh' ? "无有效数据，跳过分析。" : "No valid data, analysis skipped.";
            
            return {
                brightness: `${prefix}${this.language === 'zh' ? "亮度数据不存在" : " brightness data not available"}`,
                mainTone: `${prefix}${this.language === 'zh' ? "色调数据不存在" : " tone data not available"}`,
                localColor: `${prefix}${this.language === 'zh' ? "局部色彩数据不存在" : " local color data not available"}`,
                colorTemperature: `${prefix}${this.language === 'zh' ? "色温数据不存在" : " temperature data not available"}`,
                overallConclusion: `${prefix}${skipAnalysis}`,
                structuredConclusions: null,
            };
        }


        const structuredConclusions = {
            brightness: {
                level: analysis.L.classification.brightnessLevel,
                dynamicRange: analysis.L.classification.dynamicRange,
                skewness: analysis.L.classification.skewnessClass,
                kurtosis: analysis.L.classification.kurtosisClass,
                statistics: {
                    mean: analysis.L.statistics.mean,
                    median: analysis.L.statistics.median,
                    min: analysis.L.statistics.min,
                    max: analysis.L.statistics.max,
                    percentage: analysis.L.statistics.percentage,
                    iqr: analysis.L.statistics.iqr,
                    stdDev: analysis.L.statistics.stdDev
                }
            },
            mainTone: {
                tone: analysis.a.classification.mainTone,
                intensity: LabClassifier.classifyToneIntensity(analysis.a.classification.mainTone),
                distribution: LabClassifier.classifyColorDistribution(analysis.a.statistics.iqr),
                rules: {
                    mainTone: TONE_RULES.PARTITION_MAIN_TONE.condition({
                        mainTone: {
                            tone: analysis.a.classification.mainTone,
                            intensity: LabClassifier.classifyToneIntensity(analysis.a.classification.mainTone)
                        }
                    }),
                    intensity: TONE_RULES.PARTITION_TONE_INTENSITY.condition({
                        mainTone: {
                            tone: analysis.a.classification.mainTone,
                            intensity: LabClassifier.classifyToneIntensity(analysis.a.classification.mainTone)
                        }
                    }),
                    deviation: TONE_RULES.PARTITION_TONE_DEVIATION.condition({
                        mainTone: {
                            tone: analysis.a.classification.mainTone,
                            intensity: LabClassifier.classifyToneIntensity(analysis.a.classification.mainTone)
                        }
                    }, analysis),
                    stability: TONE_RULES.PARTITION_TONE_STABILITY.condition(analysis),
                    range: TONE_RULES.PARTITION_TONE_RANGE.condition({
                        mainTone: {
                            tone: analysis.a.classification.mainTone,
                            distribution: LabClassifier.classifyColorDistribution(analysis.a.statistics.iqr)
                        }
                    })
                },
                statistics: {
                    mean: analysis.a.statistics.mean,
                    median: analysis.a.statistics.median,
                    min: analysis.a.statistics.min,
                    max: analysis.a.statistics.max,
                    percentage: analysis.a.statistics.percentage,
                    iqr: analysis.a.statistics.iqr,
                    stdDev: analysis.a.statistics.stdDev
                }
            },
            colorTemperature: {
                tone: analysis.b.classification.colorTemperatureTone || "NEUTRAL",
                intensity: LabClassifier.classifyTemperatureIntensity(analysis.b.classification.colorTemperatureTone),
                contrast: LabClassifier.classifyTemperatureContrast(analysis.b.classification.colorTemperatureTone),
                distribution: LabClassifier.classifyColorDistribution(analysis.b.statistics.iqr),
                rules: {
                    tone: TEMPERATURE_RULES.PARTITION_TEMPERATURE_TONE.condition({
                        colorTemperature: {
                            tone: analysis.b.classification.colorTemperatureTone || "NEUTRAL",
                            intensity: LabClassifier.classifyTemperatureIntensity(analysis.b.classification.colorTemperatureTone)
                        }
                    }),
                    intensity: TEMPERATURE_RULES.PARTITION_TEMPERATURE_INTENSITY.condition({
                        colorTemperature: {
                            tone: analysis.b.classification.colorTemperatureTone || "NEUTRAL",
                            intensity: LabClassifier.classifyTemperatureIntensity(analysis.b.classification.colorTemperatureTone)
                        }
                    }, analysis),
                    stability: TEMPERATURE_RULES.PARTITION_TEMPERATURE_STABILITY.condition(analysis),
                    deviation: TEMPERATURE_RULES.PARTITION_TEMPERATURE_DEVIATION.condition({
                        colorTemperature: {
                            tone: analysis.b.classification.colorTemperatureTone || "NEUTRAL"
                        }
                    }, analysis)
                },
                statistics: {
                    mean: analysis.b.statistics.mean,
                    median: analysis.b.statistics.median,
                    min: analysis.b.statistics.min,
                    max: analysis.b.statistics.max,
                    percentage: analysis.b.statistics.percentage,
                    iqr: analysis.b.statistics.iqr,
                    stdDev: analysis.b.statistics.stdDev
                }
            },
            localColor: {
                highlights: LabClassifier.extractHighlights(analysis),
                lowlights: LabClassifier.extractLowlights(analysis),
                balance: LabClassifier.assessColorBalance(analysis),
            },
        };

        const textConclusions = this.textGenerator.generateTextDescriptions(structuredConclusions, partitionName, analysis);

        return {
            conclusions: {
                brightness: structuredConclusions.brightness,
                mainTone: structuredConclusions.mainTone,
                colorTemperature: structuredConclusions.colorTemperature,
                localColor: structuredConclusions.localColor,
                overallConclusion: textConclusions.overallConclusion,
            },
            structuredConclusions,
        };
    }

    analyzePartition(partitionData, partitionName) {
        if (!partitionData || (!partitionData.l && !partitionData.a && !partitionData.b)) {
            console.log(`[LabDetailedAnalyzer] ${partitionName} 分区数据为空或所有通道为空，返回空数据结构`);
            return {
                name: partitionName,
                L: this.analyzeChannel(null, "L"),
                a: this.analyzeChannel(null, "a"),
                b: this.analyzeChannel(null, "b"),
                conclusions: {
                    brightness: `${partitionName} 区域无数据`,
                    mainTone: `${partitionName} 区域无数据`,
                    localColor: `${partitionName} 区域无数据`,
                    colorTemperature: `${partitionName} 区域无数据`,
                    overallConclusion: `${partitionName} 区域无有效数据，无需分析。`,
                },
                structuredConclusions: {
                    brightness: {
                        level: null,
                        dynamicRange: null,
                    },
                    mainTone: {
                        tone: null,
                        intensity: null,
                        distribution: null,
                    },
                    colorTemperature: {
                        tone: "NEUTRAL",
                        intensity: null,
                        contrast: false,
                    },
                    localColor: {
                        highlights: null,
                        lowlights: null,
                        balance: null,
                    },
                },
            };
        }

        const L = this.analyzeChannel(partitionData.l, "L");
        const a = this.analyzeChannel(partitionData.a, "a");
        const b = this.analyzeChannel(partitionData.b, "b");

        const analysis = { L, a, b };
        const analysisConclusion = this.generateAnalysisConclusion(analysis, partitionName);

        return {
            name: partitionName,
            ...analysis,
            conclusions: analysisConclusion.conclusions,
            structuredConclusions: analysisConclusion.structuredConclusions,
        };
    }

    // Main analysis function
    analyze(labData) {
        if (!labData) {
            console.warn("LabDetailedAnalyzer: 输入数据为空");
            return null;
        }

        try {
            // Global analysis
            const globalResult = this.analyzePartition(labData.global, "global");
            this.analysisResults.global = globalResult;

            if (globalResult.conclusions && globalResult.conclusions.overallConclusion) {
                this.analysisResults.conclusions.global = globalResult.conclusions;
                this.analysisResults.structuredConclusions.global = globalResult.structuredConclusions;
            }

            // Partition analysis
            ["blacks", "shadows", "midtones", "highlights", "whites"].forEach(
                (partition) => {
                    const result = this.analyzePartition(labData[partition], partition);
                    this.analysisResults.partitions[partition] = result;

                    if (result.conclusions && result.conclusions.overallConclusion) {
                        this.analysisResults.conclusions.partitions[partition] = result.conclusions;
                        this.analysisResults.structuredConclusions.partitions[partition] = result.structuredConclusions;
                    }
                }
            );

            // 确保返回结果中至少包含全局分析
            if (!this.analysisResults.global) {
                console.warn("LabDetailedAnalyzer: 全局分析数据无效");
                return null;
            }

            // 生成总体结论
            if (this.analysisResults.global && this.analysisResults.partitions) {
                this.analysisResults.conclusions.overall = 
                    this.conclusionGenerator.generateOverallConclusion(
                        this.analysisResults.structuredConclusions.global,
                        this.analysisResults.structuredConclusions.partitions
                    );
            }

            return this.analysisResults;
        } catch (error) {
            console.error("LabDetailedAnalyzer: 分析过程出错:", error);
            return null;
        }
    }
}